import { Box, Button } from "@mui/material";
import React, { FC, ReactNode, useEffect, useMemo, useState } from "react";
import ApartmentIcon from "@mui/icons-material/Apartment";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import BuildingConfigComponent from "../../../common/SocietyConfig/BuildingConfig";
import { IBuildingDetails, IEditBuildingConfigurationDetails } from "../../../interfaces";
import { BUILDING_TYPES_E, CUSTOM_ENTRY_BUILDING_NAME } from "../../../utils/constants";
import CustomEntriesComponent from "../../../common/SocietyConfig/CustomEntries";
import { useGetSocietyConfigQuery } from "../../../redux/reducers/settings.reducer";
import { useNavigate } from "react-router-dom";
import { useEditBuildingConfigurationMutation } from "../../../redux/reducers/configuration.reducer";
import { useAppDispatch } from "../../../redux";
import { showFeedbackNotification } from "../../../redux/reducers/notifications.reducer";

enum SELECTED_TAB_E {
	BUILDING_CONFIG,
	CUSTOM_ENTRIES
}

type TabContentRendererType = {
	[key in SELECTED_TAB_E]: ReactNode;
};

const SocietyConfiguration: FC = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const [societyId, setSocietyId] = useState<number>(-1);
	const [selectedTab, setSelectedTab] = useState<SELECTED_TAB_E>(SELECTED_TAB_E.BUILDING_CONFIG);
	const [customEntriesList, setCustomEntriesList] = useState<string[]>([]);
	const [buildingsList, setBuildingsList] = useState<IBuildingDetails[]>([
		{
			name: "New Building",
			building_type: BUILDING_TYPES_E.BUILDING,
			entries: [],
			number_of_floors: "",
			flats_per_floor: "",
			new_building: true,
			delimiter: "-",
			show_floor_number: true
		}
	]);

	// APIS
	const { data: societyConfigResponse, refetch: refetchSocietyConfig } = useGetSocietyConfigQuery(null, {
		refetchOnMountOrArgChange: true
	});
	const societyConfigData = useMemo(() => societyConfigResponse?.results ?? [], [societyConfigResponse?.results]);

	const initialBuildingDetails = useMemo<{
		buildingsList: IBuildingDetails[];
		customEntriesList: string[];
	}>(() => {
		const updatedInitialBuildingsList: IBuildingDetails[] = [];
		const updatedInitialCustomEntriesList: string[] = [];

		if (societyConfigData && societyConfigData.length > 0) {
			for (const buildingItem of societyConfigData[0].building_config) {
				if (buildingItem.building_type === BUILDING_TYPES_E.BUILDING) {
					updatedInitialBuildingsList.push({
						name: buildingItem.name,
						building_type: buildingItem.building_type,
						entries: buildingItem.entries.map(
							(entryItem) => `${buildingItem.name}${buildingItem.delimiter || "-"}${entryItem}`
						),
						number_of_floors: String(buildingItem.floors),
						flats_per_floor: String(buildingItem.flats),
						new_building: false,
						delimiter: buildingItem.delimiter || "-",
						show_floor_number: buildingItem.show_floor_number ?? false
					});
				} else if (buildingItem.building_type === BUILDING_TYPES_E.CUSTOM_ENTRIES) {
					updatedInitialCustomEntriesList.push(...buildingItem.entries);
				}
			}
		}

		return {
			buildingsList: updatedInitialBuildingsList,
			customEntriesList: updatedInitialCustomEntriesList
		};
	}, [societyConfigData]);

	const [
		editBuildingConfig,
		{
			isLoading: editBuildingConfigLoading,
			error: editBuildingConfigError,
			isSuccess: editBuildingConfigSuccess,
			data: editBuildingConfigResponse,
			reset: resetEditBuildingConfigState
		}
	] = useEditBuildingConfigurationMutation();

	function handleChangeSelectedTab(newTab: SELECTED_TAB_E) {
		setSelectedTab(newTab);
	}

	function handleDeleteBuilding(index: number) {
		if (index >= 0) {
			setBuildingsList((currentBuildingsList) => {
				const updatedBuildingsList = [...currentBuildingsList];
				updatedBuildingsList.splice(index, 1);
				return updatedBuildingsList;
			});
		}
	}

	function handleSaveBuildingDetails(index: number, updatedBuildingDetails: IBuildingDetails) {
		if (index >= 0) {
			const updatedBuildingsList = [...buildingsList];
			updatedBuildingsList[index] = updatedBuildingDetails;
			setBuildingsList(updatedBuildingsList);

			handleSubmitButtonClick(updatedBuildingsList);
		}
	}

	function handleAddNewBuilding(): number {
		const updatedBuildingsList = [...buildingsList];

		const updatedListLength = updatedBuildingsList.push({
			name: "New Building",
			building_type: BUILDING_TYPES_E.BUILDING,
			entries: [],
			number_of_floors: "",
			flats_per_floor: "",
			new_building: true,
			delimiter: "-",
			show_floor_number: true
		});

		setBuildingsList(updatedBuildingsList);

		return updatedListLength - 1;
	}

	function handleUpdateCustomEntries(newEntries: string[]) {
		setCustomEntriesList(newEntries);
	}

	function handleBackButtonClick() {
		navigate(-1);
	}

	function handleNextButtonClick() {
		navigate("/ots/custom-fields");
	}

	function handleSubmitButtonClick(updatedBuildingsList: IBuildingDetails[]) {
		if (societyId < 0) return;

		const finalBuildingConfig: IEditBuildingConfigurationDetails[] = [];

		for (const buildingItem of updatedBuildingsList) {
			if (!buildingItem.new_building) {
				finalBuildingConfig.push({
					name: buildingItem.name,
					building_type: buildingItem.building_type,
					entries: buildingItem.entries.map((entryItem) =>
						entryItem.replace(buildingItem.name, "").replace(buildingItem.delimiter, "")
					),
					floors: buildingItem.number_of_floors,
					flats: buildingItem.flats_per_floor,
					delimiter: buildingItem.delimiter,
					show_floor_number: buildingItem.show_floor_number
				});
			}
		}

		if (customEntriesList.length > 0) {
			finalBuildingConfig.push({
				name: CUSTOM_ENTRY_BUILDING_NAME,
				building_type: BUILDING_TYPES_E.CUSTOM_ENTRIES,
				entries: customEntriesList
			});
		}

		// if (finalBuildingConfig.length > 0) {
		editBuildingConfig({
			id: societyId,
			data: {
				building_config: finalBuildingConfig
			}
		});
		// }
	}

	function handleSubmitCustomEntries() {
		handleSubmitButtonClick(buildingsList);
	}

	useEffect(() => {
		if (societyConfigData && societyConfigData.length > 0) {
			const societyDetails = societyConfigData[0];

			if (societyDetails.building_config.length > 0) {
				// navigate("/ots/add-camera");

				const updatedBuildingsList: IBuildingDetails[] = [];
				const updatedCustomEntriesList: string[] = [];

				for (const buildingItem of societyDetails.building_config) {
					if (buildingItem.building_type === BUILDING_TYPES_E.BUILDING) {
						updatedBuildingsList.push({
							name: buildingItem.name,
							building_type: buildingItem.building_type,
							entries: buildingItem.entries.map(
								(entryItem) => `${buildingItem.name}${buildingItem.delimiter || "-"}${entryItem}`
							),
							number_of_floors: String(buildingItem.floors),
							flats_per_floor: String(buildingItem.flats),
							new_building: false,
							delimiter: buildingItem.delimiter || "-",
							show_floor_number: buildingItem.show_floor_number ?? false
						});
					} else if (buildingItem.building_type === BUILDING_TYPES_E.CUSTOM_ENTRIES) {
						updatedCustomEntriesList.push(...buildingItem.entries);
					}
				}

				setBuildingsList(updatedBuildingsList);
				setCustomEntriesList(updatedCustomEntriesList);
			} else {
				setSocietyId(societyDetails.id);
			}
		}
	}, [societyConfigData]);

	useEffect(() => {
		if (editBuildingConfigError && "data" in editBuildingConfigError) {
			resetEditBuildingConfigState();

			dispatch(
				showFeedbackNotification({
					message: editBuildingConfigError.data ? String(editBuildingConfigError.data) : "Something went wrong",
					severity: "error"
				})
			);
		}
	}, [dispatch, editBuildingConfigError, resetEditBuildingConfigState]);

	useEffect(() => {
		if (editBuildingConfigSuccess && editBuildingConfigResponse) {
			resetEditBuildingConfigState();
			// navigate("/ots/custom-fields");
			refetchSocietyConfig();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editBuildingConfigSuccess, editBuildingConfigResponse, resetEditBuildingConfigState, navigate]);

	const RenderTabContent: TabContentRendererType = {
		[SELECTED_TAB_E.BUILDING_CONFIG]: (
			<BuildingConfigComponent
				buildingsList={buildingsList}
				initialBuildingsList={initialBuildingDetails.buildingsList}
				saveButtonLoading={editBuildingConfigLoading}
				onDeleteBuilding={handleDeleteBuilding}
				onSaveBuilding={handleSaveBuildingDetails}
				onAddBuilding={handleAddNewBuilding}
				onCancelButtonClick={handleBackButtonClick}
				onNextButtonClick={handleNextButtonClick}
			/>
		),
		[SELECTED_TAB_E.CUSTOM_ENTRIES]: (
			<CustomEntriesComponent
				customEntriesList={customEntriesList}
				initialCustomEntriesList={initialBuildingDetails.customEntriesList}
				saveButtonLoading={editBuildingConfigLoading}
				onUpdateCustomEntries={handleUpdateCustomEntries}
				onSave={handleSubmitCustomEntries}
				onCancelButtonClick={handleBackButtonClick}
				onNextButtonClick={handleNextButtonClick}
			/>
		)
	};

	return (
		<Box className="ots-screen-content-wrapper">
			<Box className="ots-society-config-content-wrapper">
				<Box className="ots-society-config-header-wrapper">
					<Button
						disableElevation
						variant={selectedTab === SELECTED_TAB_E.BUILDING_CONFIG ? "contained" : "outlined"}
						color="primary"
						size="large"
						startIcon={<ApartmentIcon />}
						onClick={() => handleChangeSelectedTab(SELECTED_TAB_E.BUILDING_CONFIG)}
						sx={{ textTransform: "capitalize" }}
					>
						Building Configuration
					</Button>

					<Button
						disableElevation
						variant={selectedTab === SELECTED_TAB_E.CUSTOM_ENTRIES ? "contained" : "outlined"}
						color="primary"
						size="large"
						startIcon={<DashboardCustomizeIcon />}
						onClick={() => handleChangeSelectedTab(SELECTED_TAB_E.CUSTOM_ENTRIES)}
						sx={{ textTransform: "capitalize" }}
					>
						Custom Entries
					</Button>
				</Box>

				<Box className="ots-society-config-tab-content">{RenderTabContent[selectedTab]}</Box>
			</Box>

			{/* <Box className="ots-form-actions-wrapper">
				<Button variant="outlined" color="error" sx={{ paddingX: 5 }} onClick={handleBackButtonClick}>
					Back
				</Button>

				<LoadingButton
					disableElevation
					variant="contained"
					color="primary"
					onClick={handleSubmitButtonClick}
					loading={editBuildingConfigLoading}
				>
					Save and Next
				</LoadingButton>
			</Box> */}
		</Box>
	);
};

export default SocietyConfiguration;
